import { toUint8Array } from './bytes.js';
import { unlockClientDek } from './keyBundle.js';

const DEFAULT_IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const unlockedClientKeys = new Map();
let idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS;
let lastActivityMs = Date.now();
let activityInitialized = false;
let idleTimerId = null;

function markActivity() {
  lastActivityMs = Date.now();
}

function clearIdleTimer() {
  if (idleTimerId) {
    clearInterval(idleTimerId);
    idleTimerId = null;
  }
}

function enforceIdleTimeout() {
  if (Date.now() - lastActivityMs > idleTimeoutMs) {
    unlockedClientKeys.clear();
  }
}

function initializeActivityTracking() {
  if (activityInitialized || typeof window === 'undefined') {
    return;
  }

  const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
  for (const eventName of events) {
    window.addEventListener(eventName, markActivity, { passive: true });
  }

  window.addEventListener('focus', markActivity);
  idleTimerId = setInterval(enforceIdleTimeout, 30 * 1000);
  activityInitialized = true;
}

function assertClientId(clientId) {
  if (typeof clientId !== 'string' || !clientId.trim()) {
    throw new Error('clientId is required.');
  }
}

export function setSessionIdleTimeoutMs(timeoutMs) {
  if (!Number.isInteger(timeoutMs) || timeoutMs <= 0) {
    throw new Error('timeoutMs must be a positive integer.');
  }
  idleTimeoutMs = timeoutMs;
  markActivity();
}

export function setClientSessionKey(clientId, dekBytes) {
  assertClientId(clientId);
  const normalizedDek = toUint8Array(dekBytes, 'dekBytes');
  if (normalizedDek.length !== 32) {
    throw new Error('Client DEK must be 32 bytes.');
  }

  initializeActivityTracking();
  markActivity();
  unlockedClientKeys.set(clientId, normalizedDek);
}

export function getClientSessionKey(clientId) {
  assertClientId(clientId);
  initializeActivityTracking();
  enforceIdleTimeout();
  markActivity();
  return unlockedClientKeys.get(clientId) || null;
}

export function isClientUnlocked(clientId) {
  return Boolean(getClientSessionKey(clientId));
}

export function lockClient(clientId) {
  assertClientId(clientId);
  unlockedClientKeys.delete(clientId);
}

export function lockAllClients() {
  unlockedClientKeys.clear();
  clearIdleTimer();
  activityInitialized = false;
}

export async function unlockClientWithPassphrase(clientRecord, passphrase) {
  const clientId = clientRecord?.client_id;
  assertClientId(clientId);

  if (!clientRecord?.key_bundle) {
    throw new Error('Client key bundle is not configured.');
  }

  const dekBytes = await unlockClientDek({
    clientId,
    passphrase,
    keyBundle: clientRecord.key_bundle,
  });

  setClientSessionKey(clientId, dekBytes);
  return dekBytes;
}
